import { motion } from "framer-motion";
import {
  Globe,
  Smartphone,
  Cpu,
  Copyright,
  BadgeCheck,
  BarChart3,
  Megaphone,
  ArrowUpRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import { SERVICES, INDUSTRIES, TESTIMONIALS, BRAND } from "../lib/constants";
import SectionHeader from "../components/SectionHeader";
import CTASection from "../components/CTASection";
import { Reveal, Stagger, itemFade } from "../components/Motion";

const ICONS = {
  "website-development": Globe,
  "app-development": Smartphone,
  "ai-automation": Cpu,
  "copyright-strategy": Copyright,
  "trademark-strategy": BadgeCheck,
  "benchmark-intelligence": BarChart3,
  "marketing-growth": Megaphone,
};

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_721da108-dae5-4d17-b75f-0c65d5e29a3d/artifacts/yh00ua8h_32c21a19cb60dda3126ac8f0efecd5a952eae60f451402dc9796d7fd410eb1d6.png";

export default function Home() {
  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative min-h-[100vh] flex items-end pt-32 pb-24 overflow-hidden"
      >
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="Elevara by Innapax"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 hero-overlay" />
          <div className="absolute inset-0 hero-overlay-bottom" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-8 w-full">
          <div className="grid lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8">
              <Reveal>
                <div className="flex items-center gap-3 mb-8">
                  <span className="pulse-dot" />
                  <span className="overline">
                    Premium Digital Growth Agency
                  </span>
                </div>
              </Reveal>

              <Reveal delay={0.1}>
                <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-medium leading-[0.95] tracking-tight">
                  Transform Ideas
                  <br />
                  Into{" "}
                  <em className="text-[#D4AF37] not-italic">Digital Assets</em>.
                </h1>
              </Reveal>

              <Reveal delay={0.2}>
                <p className="mt-8 max-w-2xl text-white/70 text-base md:text-lg leading-relaxed">
                  Website Development, Mobile Apps, AI Automation, Copyright
                  Protection, Trademark Strategy, Competitive Benchmarking, and
                  Growth Marketing — engineered with luxury, delivered with
                  rigor.
                </p>
              </Reveal>

              <Reveal delay={0.3}>
                <div className="mt-10 flex flex-wrap items-center gap-4">
                  <Link
                    to="/contact"
                    data-testid="hero-cta-start-project"
                    className="btn-gold text-sm"
                  >
                    Start Your Project
                    <ArrowUpRight size={16} />
                  </Link>
                  <Link
                    to="/contact"
                    data-testid="hero-cta-consultation"
                    className="btn-ghost text-sm"
                  >
                    Schedule Consultation
                  </Link>
                </div>
              </Reveal>
            </div>

            <Reveal delay={0.5} className="lg:col-span-4">
              <div className="border-l border-[#D4AF37]/30 pl-6 space-y-6">
                {[
                  { k: "100+", v: "Projects Delivered" },
                  { k: "95%", v: "Client Satisfaction" },
                  { k: "24/7", v: "Premium Support" },
                ].map((s) => (
                  <div key={s.k} data-testid={`hero-stat-${s.k}`}>
                    <div className="font-display text-3xl md:text-4xl text-[#D4AF37]">
                      {s.k}
                    </div>
                    <div className="text-xs uppercase tracking-[0.18em] text-white/50 mt-2">
                      {s.v}
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* TICKER STRIP */}
      <section className="border-y border-white/5 py-6 overflow-hidden bg-[#0E0E0E]">
        <div className="flex marquee whitespace-nowrap gap-16 text-white/30 font-display text-2xl md:text-3xl">
          {[...Array(2)].map((_, idx) => (
            <div key={idx} className="flex items-center gap-16 shrink-0">
              {[
                "ELEVATE",
                "★",
                "AUTOMATE",
                "★",
                "ACCELERATE",
                "★",
                "ENGINEER",
                "★",
                "PROTECT",
                "★",
                "GROW",
                "★",
              ].map((w, i) => (
                <span
                  key={i}
                  className={w === "★" ? "text-[#D4AF37]/60" : ""}
                >
                  {w}
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES OVERVIEW */}
      <section
        data-testid="services-overview"
        className="py-24 md:py-32 px-6 md:px-8"
      >
        <div className="max-w-7xl mx-auto">
          <SectionHeader
            overline="What we do"
            title="Seven disciplines. One partner."
            subtitle="Each capability is led by senior operators — engineered to deliver outsized outcomes at a luxury standard."
            link="/services"
            linkLabel="Explore all services"
          />

          <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 border border-white/5">
            {SERVICES.slice(0, 6).map((s) => {
              const Icon = ICONS[s.slug] || Globe;
              return (
                <motion.div
                  key={s.slug}
                  variants={itemFade}
                  data-testid={`service-card-${s.slug}`}
                  className="bg-[#0B0B0B] p-10 group hover:bg-[#141414] transition-colors duration-500"
                >
                  <div className="flex items-start justify-between mb-10">
                    <div className="w-12 h-12 border border-[#D4AF37]/30 flex items-center justify-center group-hover:bg-[#D4AF37] transition-colors duration-300">
                      <Icon
                        size={20}
                        strokeWidth={1.5}
                        className="text-[#D4AF37] group-hover:text-[#0B0B0B]"
                      />
                    </div>
                    <span className="font-mono text-xs text-white/30">
                      0{SERVICES.indexOf(s) + 1}
                    </span>
                  </div>
                  <h3 className="font-display text-2xl md:text-[26px] font-medium mb-4">
                    {s.name}
                  </h3>
                  <p className="text-white/55 text-sm leading-relaxed mb-8">
                    {s.short}
                  </p>
                  <Link
                    to="/services"
                    className="inline-flex items-center gap-2 text-[#D4AF37] text-xs uppercase tracking-[0.18em] hover:text-[#F0C84B]"
                  >
                    Learn More <ArrowUpRight size={14} />
                  </Link>
                </motion.div>
              );
            })}
          </Stagger>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section
        data-testid="why-choose"
        className="py-24 md:py-32 px-6 md:px-8 bg-[#0E0E0E] border-y border-white/5"
      >
        <div className="max-w-7xl mx-auto">
          <SectionHeader
            overline="Why Elevara"
            title="The standard our clients expect."
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-20">
            {[
              {
                num: "01",
                title: "Strategic Consulting",
                desc: "McKinsey-style thinking applied to your specific market reality.",
              },
              {
                num: "02",
                title: "Technology Excellence",
                desc: "Senior engineers, production systems, zero compromise on quality.",
              },
              {
                num: "03",
                title: "AI-Powered Efficiency",
                desc: "Custom agents and automations that compound your margin.",
              },
              {
                num: "04",
                title: "Long-Term Partnership",
                desc: "Built to scale with you — not project-to-project.",
              },
            ].map((c) => (
              <Reveal key={c.num} delay={parseInt(c.num) * 0.05}>
                <div className="border-t border-[#D4AF37]/20 pt-6">
                  <div className="font-mono text-xs text-[#D4AF37] mb-6">
                    / {c.num}
                  </div>
                  <h3 className="font-display text-xl md:text-2xl mb-4 leading-tight">
                    {c.title}
                  </h3>
                  <p className="text-white/55 text-sm leading-relaxed">
                    {c.desc}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 border border-white/5">
            {[
              { k: "100+", v: "Projects Delivered" },
              { k: "95%", v: "Client Satisfaction" },
              { k: "24/7", v: "Support" },
            ].map((s, i) => (
              <div
                key={s.k}
                data-testid={`metric-${i}`}
                className={`p-10 md:p-14 ${i < 2 ? "md:border-r border-white/5" : ""}`}
              >
                <div className="font-display text-5xl md:text-6xl text-[#D4AF37] tracking-tight">
                  {s.k}
                </div>
                <div className="mt-4 text-xs uppercase tracking-[0.22em] text-white/50">
                  {s.v}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section
        data-testid="industries"
        className="py-24 md:py-32 px-6 md:px-8"
      >
        <div className="max-w-7xl mx-auto">
          <SectionHeader
            overline="Industries"
            title="Trusted across six categories."
            subtitle="Specialized practices for the verticals where premium execution wins."
          />

          <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 border border-white/5">
            {INDUSTRIES.map((ind, i) => (
              <motion.div
                key={ind}
                variants={itemFade}
                data-testid={`industry-${i}`}
                className="bg-[#0B0B0B] p-10 group hover:bg-[#141414] transition-colors cursor-default"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-mono text-xs text-white/30">
                      0{i + 1}
                    </span>
                    <h3 className="font-display text-2xl md:text-[26px] mt-3 group-hover:text-[#D4AF37] transition-colors">
                      {ind}
                    </h3>
                  </div>
                  <ArrowUpRight
                    size={20}
                    className="text-white/20 group-hover:text-[#D4AF37] group-hover:rotate-45 transition-all duration-500"
                  />
                </div>
              </motion.div>
            ))}
          </Stagger>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section
        data-testid="testimonials"
        className="py-24 md:py-32 px-6 md:px-8 bg-[#0E0E0E] border-y border-white/5"
      >
        <div className="max-w-7xl mx-auto">
          <SectionHeader
            overline="Said about us"
            title="Quietly remarkable feedback."
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/5 border border-white/5">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div
                  data-testid={`testimonial-${i}`}
                  className="bg-[#0B0B0B] p-10 md:p-14 h-full flex flex-col justify-between"
                >
                  <div>
                    <div className="text-[#D4AF37] font-display text-5xl leading-none mb-6">
                      &ldquo;
                    </div>
                    <p className="text-white/85 text-lg md:text-xl leading-relaxed font-display font-medium">
                      {t.quote}
                    </p>
                  </div>
                  <div className="mt-10 pt-6 border-t border-white/5">
                    <div className="text-sm font-medium">{t.author}</div>
                    <div className="text-xs uppercase tracking-[0.18em] text-[#D4AF37]/80 mt-1">
                      {t.role}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </main>
  );
}
